import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-hot-toast';
import {
  Search,
  Plus,
  Eye,
  Trash2,
  CheckCircle,
  XCircle,
  Car,
  Building,
  FileText,
  AlertTriangle,
  CheckSquare,
  XSquare,
  Download
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import BlockVehicleModal from '../../components/BlockVehicleModal';
import VehicleDocumentViewer from '../../components/VehicleDocumentViewer';
import usePersistentFilters from '../../hooks/usePersistentFilters';

const Vehicles = () => {
  const { user } = useAuth();
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [documentVehicle, setDocumentVehicle] = useState(null);

  const {
    filters,
    handleFilterChange,
    handlePageChange,
    clearFilters,
  } = usePersistentFilters({
    search: '',
    isApproved: '',
    isBlocked: '',
    isActive: '',
    fuelType: '',
    type: '',
    vendorId: '',
    developerOnly: 'Exclude',
    page: 1,
    limit: 10,
    sortBy: 'createdOn',
    sortOrder: 'desc'
  }, 'vehicles-filters');

  // Fetch vehicles
  const { data: vehiclesData, isLoading, isFetching, error, refetch } = useQuery(
    ['vehicles', filters],
    async () => {
      const response = await api.get('/vehicles', { params: filters });
      return response;
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: 0, // Always consider data stale
      cacheTime: 0  // Don't cache
    }
  );

  // Fetch vendors for filter
  const { data: vendorsData } = useQuery(
    ['vendors', 'all'],
    () => api.get('/vendors', { params: { limit: 1000, isApproved: 'true' } }),
    {
      staleTime: 300000 // 5 minutes
    }
  );

  const handleApproveVehicle = async (vehicleId) => {
    try {
      await api.patch(`/vehicles/${vehicleId}/approve`, { approvalReason: 'Approved by admin' });
      toast.success('Vehicle approved successfully');
      refetch();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to approve vehicle');
    }
  };

  const handleBlockVehicle = async (blockData) => {
    try {
      await api.patch(`/vehicles/${blockData.vehicleId}/block`, blockData);
      toast.success(`Vehicle ${blockData.isBlocked ? 'blocked' : 'unblocked'} successfully`);
      refetch();
      setShowBlockModal(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update vehicle status');
    }
  };

  const handleDeleteVehicle = async (vehicleId) => {
    try {
      await api.delete(`/vehicles/${vehicleId}`);
      toast.success('Vehicle deleted successfully');
      refetch();
      setShowDeleteConfirm(false);
      setVehicleToDelete(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete vehicle');
    }
  };

  const handleToggleActive = async (vehicleId, currentStatus) => {
    try {
      await api.patch(`/vehicles/${vehicleId}/toggle-active`, {
        isActive: !currentStatus
      });
      toast.success(`Vehicle ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      refetch();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update vehicle status');
    }
  };

  const handleApproveVehicleConfirm = async (vehicleId) => {
    if (window.confirm('Are you sure you want to approve this vehicle?')) {
      await handleApproveVehicle(vehicleId);
    }
  };

  const handleDeleteVehicleConfirm = (vehicle) => {
    setVehicleToDelete(vehicle);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (vehicleToDelete) {
      await handleDeleteVehicle(vehicleToDelete._id);
    }
  };

  const getStatusBadge = (vehicle) => {
    if (vehicle.isDeleted) {
      return <span className="badge badge-error">Deleted</span>;
    }
    if (vehicle.isBlocked) {
      return <span className="badge badge-error">Blocked</span>;
    }
    if (vehicle.isApproved) {
      return <span className="badge badge-success">Approved</span>;
    }
    return <span className="badge badge-warning">Pending</span>;
  };

  const getFuelBadge = (fuelType) => {
    const fuelColors = {
      'PETROL': 'badge-info',
      'DIESEL': 'badge-warning',
      'CNG': 'badge-success',
      'ELECTRIC': 'badge-primary',
      'HYBRID': 'badge-secondary'
    };
    return <span className={`badge ${fuelColors[fuelType] || 'badge-secondary'}`}>{fuelType}</span>;
  };

  const getTypeBadge = (type) => {
    const typeColors = {
      'HATCHBACK': 'badge-info',
      'SEDAN': 'badge-primary',
      'SUV': 'badge-warning',
      'MUV': 'badge-success',
      'LUXURY': 'badge-error',
      'BIKE': 'badge-secondary',
      'AUTO': 'badge-info',
      'ELECTRIC_AUTO': 'badge-success'
    };
    return <span className={`badge ${typeColors[type] || 'badge-secondary'}`}>{type}</span>;
  };

  const formatExpiryDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    // Convert timestamp to milliseconds if it's in seconds
    const ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;
    const date = new Date(ms);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((date - today) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return <span className="text-red-600 font-medium">Expired {Math.abs(daysUntilExpiry)} days ago</span>;
    } else if (daysUntilExpiry <= 30) {
      return <span className="text-yellow-600 font-medium">Expires in {daysUntilExpiry} days</span>;
    } else {
      return <span className="text-green-600">{date.toLocaleDateString()}</span>;
    }
  };

  if (error) {
    return (
      <div className="page-content">
        <div className="text-center py-8">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Vehicles</h2>
          <p className="text-gray-600 mb-4">{error.message}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-6 py-5 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center text-primary-600 shadow-sm">
              <Car className="h-5 w-5" />
            </div>
            <div>
              <h1 className="page-title leading-tight">Vehicles</h1>
              <p className="page-description">
                Manage and monitor all vehicles in the system
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {user?.permissions?.vehicles?.write && (
              <Link to="/vehicles/create" className="btn btn-primary shadow-sm hover:shadow transition-all">
                <Plus className="h-4 w-4 mr-2" />
                Add Vehicle
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="card-title">Filters</h3>
            <button
              onClick={clearFilters}
              className="btn btn-outline btn-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label className="form-label">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search vehicles..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>

            {/* Approval Filter */}
            <div>
              <label className="form-label">Approval</label>
              <select
                value={filters.isApproved}
                onChange={(e) => handleFilterChange('isApproved', e.target.value)}
                className="input"
              >
                <option value="">All</option>
                <option value="true">Approved</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            {/* Blocked Filter */}
            <div>
              <label className="form-label">Blocked</label>
              <select
                value={filters.isBlocked}
                onChange={(e) => handleFilterChange('isBlocked', e.target.value)}
                className="input"
              >
                <option value="">All</option>
                <option value="true">Blocked</option>
                <option value="false">Active</option>
              </select>
            </div>

            {/* Active Filter */}
            <div>
              <label className="form-label">Active</label>
              <select
                value={filters.isActive}
                onChange={(e) => handleFilterChange('isActive', e.target.value)}
                className="input"
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            {/* Fuel Type Filter */}
            <div>
              <label className="form-label">Fuel Type</label>
              <select
                value={filters.fuelType}
                onChange={(e) => handleFilterChange('fuelType', e.target.value)}
                className="input"
              >
                <option value="">All</option>
                <option value="PETROL">Petrol</option>
                <option value="DIESEL">Diesel</option>
                <option value="CNG">CNG</option>
                <option value="PETROL + CNG">PETROL + CNG</option>
                <option value="EV">Electric</option>
                <option value="HYBRID">Hybrid</option>
              </select>
            </div>

            {/* Vehicle Type Filter */}
            <div>
              <label className="form-label">Vehicle Type</label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="input"
              >
                <option value="">All</option>
                <option value="AUTO">Auto</option>
                <option value="ELECTRIC_AUTO">Electric Auto</option>
                <option value="HATCHBACK">Hatchback</option>
                <option value="SEDAN">Sedan</option>
                <option value="SUV">SUV</option>
                <option value="MUV">MUV</option>
                <option value="LUXURY">Luxury</option>
                <option value="BIKE">Bike</option>
              </select>
            </div>

            {/* Vendor Filter */}
            <div>
              <label className="form-label">Vendor</label>
              <select
                value={filters.vendorId}
                onChange={(e) => handleFilterChange('vendorId', e.target.value)}
                className="input"
              >
                <option value="">All Vendors</option>
                {vendorsData?.data?.vendors?.map((vendor) => (
                  <option key={vendor._id} value={vendor._id}>
                    {vendor.VendorName}
                  </option>
                ))}
              </select>
            </div>

            {/* Developer Filter */}
            <div>
              <label className="form-label">Developer Filter</label>
              <select
                value={filters.developerOnly}
                onChange={(e) => handleFilterChange('developerOnly', e.target.value)}
                className="input"
              >
                <option value="Exclude">Exclude Developers</option>
                <option value="Include">Include Developers</option>
                <option value="Only">Developers Only</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Vehicles Table */}
      <div className="card mt-6">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Vehicles ({vehiclesData?.data?.data?.pagination?.totalRecords || 0})
            </h3>
            <button className="btn btn-outline">
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
          </div>
        </div>
        <div className="card-body">
          {(isLoading || isFetching) ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading vehicles...</p>
            </div>
          ) : !vehiclesData?.data?.data?.vehicles || vehiclesData?.data?.data?.vehicles?.length === 0 ? (
            <div className="empty-state">
              <Car className="empty-state-icon" />
              <h3 className="empty-state-title">No vehicles found</h3>
              <p className="empty-state-description">
                {filters.search || filters.isApproved || filters.isBlocked || filters.isActive || filters.fuelType || filters.type || filters.vendorId
                  ? 'Try adjusting your filters to see more results.'
                  : 'Get started by adding your first vehicle.'}
              </p>
              {user?.permissions?.vehicles?.write && (
                <Link to="/vehicles/create" className="btn btn-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Vehicle
                </Link>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Vehicle</th>
                    <th className="table-header-cell">Vendor</th>
                    <th className="table-header-cell">Registration</th>
                    <th className="table-header-cell">Fuel/Type</th>
                    <th className="table-header-cell">Expiry Dates</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {vehiclesData?.data?.data?.vehicles?.map((vehicle) => (
                    <tr key={vehicle._id} className="table-row">
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <Car className="h-5 w-5 text-primary-700" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {vehicle.make} {vehicle.model}
                            </div>
                            <div className="text-sm text-gray-500">
                              {vehicle.fleetSysId}
                            </div>
                            <div className="text-sm text-gray-500">
                              {vehicle.year} • {vehicle.color}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center">
                          <Building className="h-4 w-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm text-gray-900">
                              {vehicle.vendorId?.VendorName || 'N/A'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {vehicle.vendorId?.fleetSysId || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">{vehicle.regNo}</div>
                        <div className="text-sm text-gray-500">Permit: {vehicle.permitNumber}</div>
                      </td>
                      <td className="table-cell">
                        <div className="flex flex-col space-y-1">
                          {getFuelBadge(vehicle.fuelType)}
                          {getTypeBadge(vehicle.type)}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="space-y-1 text-sm">
                          <div>
                            <span className="text-gray-500">Insurance:</span> {formatExpiryDate(vehicle.insuranceExpiry)}
                          </div>
                          <div>
                            <span className="text-gray-500">Fitness:</span> {formatExpiryDate(vehicle.fitnessExpiry)}
                          </div>
                          <div>
                            <span className="text-gray-500">PUC:</span> {formatExpiryDate(vehicle.pucExpiry)}
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex flex-col space-y-1">
                          {getStatusBadge(vehicle)}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <Link
                            to={`/vehicles/${vehicle._id}`}
                            className="text-primary-600 hover:text-primary-900"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Link>

                          <button
                            onClick={() => {
                              setDocumentVehicle(vehicle);
                              setShowDocumentViewer(true);
                            }}
                            className="text-info-600 hover:text-info-900"
                            title="View Documents"
                          >
                            <FileText className="h-4 w-4" />
                          </button>

                          {user?.permissions?.vehicles?.write && !vehicle.isApproved && (
                            <button
                              onClick={() => handleApproveVehicleConfirm(vehicle._id)}
                              className="text-success-600 hover:text-success-900"
                              title="Approve Vehicle"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                          )}

                          {user?.permissions?.vehicles?.write && (
                            <button
                              onClick={() => {
                                setSelectedVehicle(vehicle);
                                setShowBlockModal(true);
                              }}
                              className={vehicle.isBlocked ? "text-success-600 hover:text-success-900" : "text-warning-600 hover:text-warning-900"}
                              title={vehicle.isBlocked ? "Unblock Vehicle" : "Block Vehicle"}
                            >
                              {vehicle.isBlocked ? (
                                <CheckCircle className="h-4 w-4" />
                              ) : (
                                <XCircle className="h-4 w-4" />
                              )}
                            </button>
                          )}

                          {user?.permissions?.vehicles?.write && (
                            <button
                              onClick={() => handleToggleActive(vehicle._id, vehicle.isActive)}
                              className={vehicle.isActive ? "text-success-600 hover:text-success-900" : "text-warning-600 hover:text-warning-900"}
                              title={vehicle.isActive ? "Deactivate Vehicle" : "Activate Vehicle"}
                            >
                              {vehicle.isActive ? (
                                <CheckSquare className="h-4 w-4" />
                              ) : (
                                <XSquare className="h-4 w-4" />
                              )}
                            </button>
                          )}

                          {user?.permissions?.vehicles?.delete && (
                            <button
                              onClick={() => handleDeleteVehicleConfirm(vehicle)}
                              className="text-danger-600 hover:text-danger-900"
                              title="Delete Vehicle"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {vehiclesData?.data?.data?.pagination && vehiclesData.data.data.pagination.total > 1 && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              {isFetching
                ? `Loading page ${filters.page}...`
                : (
                  <>
                    Showing {((vehiclesData.data.data.pagination.current - 1) * vehiclesData.data.data.pagination.limit) + 1} to{' '}
                    {Math.min(vehiclesData.data.data.pagination.current * vehiclesData.data.data.pagination.limit, vehiclesData.data.data.pagination.totalRecords)} of{' '}
                    {vehiclesData.data.data.pagination.totalRecords} results
                  </>
                )}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(Number(filters.page) - 1)}
                disabled={isFetching || Number(filters.page) === 1}
                className="btn btn-outline btn-sm"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(Number(filters.page) + 1)}
                disabled={isFetching || Number(filters.page) === vehiclesData.data.data.pagination.total}
                className="btn btn-outline btn-sm"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Block Vehicle Modal */}
      <BlockVehicleModal
        isOpen={showBlockModal}
        onClose={() => setShowBlockModal(false)}
        vehicle={selectedVehicle}
        onBlock={handleBlockVehicle}
      />

      {/* Document Viewer Modal */}
      <VehicleDocumentViewer
        isOpen={showDocumentViewer}
        onClose={() => {
          setShowDocumentViewer(false);
          setDocumentVehicle(null);
        }}
        vehicle={documentVehicle}
      />


      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Delete Vehicle</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the vehicle "{vehicleToDelete?.regNo}"? This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn btn-outline flex-1"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="btn btn-danger flex-1"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vehicles; 